import { FC, useContext } from 'react';
import { i18n } from '@lingui/core';
import { ActionIcon, Group, Text } from '@mantine/core';
import { IconBallpen, IconDragDrop, IconDragDrop2, IconTrashX } from '@tabler/icons';
import { useDrag, useDrop } from 'react-dnd-latest';
import { CustomEventState } from '../../Types/EventT';
import Dot from '../../Atoms/Dot';
import { EventS } from '../../Service/EventS';
import { ToastS } from '../../Service/ToastS';
import { WindowContext } from '../../Service/Context/WindowContext';
import { ViolationContext } from '../../Service/Context/ViolationContext';
import { LicenceS } from '../../Service/LicenceS';
import { openCreateEventStatusModal, openDeleteEventStateModal, openEditEventStatusModal } from './EventStatusesModals';

interface Props {
  state: CustomEventState;
  refresh: () => void;
  states: CustomEventState[];
}

const EventStateItem: FC<Props> = ({ state, refresh, states }) => {
  const { id, idx, label, color, originState, primary, emoji } = state;
  const { xs, lg } = useContext(WindowContext);
  const { executeWithLicenceCheck } = useContext(ViolationContext);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'eventState',
      item: state,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
        handlerId: monitor.getHandlerId(),
      }),
    }),
    [idx],
  );

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: 'eventState',
      canDrop: (item: any) => item.id !== id,
      collect: (monitor: any) => ({
        isOver: monitor.isOver() && monitor.getItem().id !== id,
        canDrop: !!monitor.canDrop(),
      }),

      drop(item: any) {
        if (item.originState === originState && idx !== null) {
          EventS.moveCustomState(originState, item.idx, idx).then((ok) => {
            if (ok) {
              refresh();
            }
          });
        }
        if (item.originState !== originState) {
          ToastS.warn('drop-not-supported', 'Der Status kann nicht zu einem anderen Basisstatus verschoben werden.');
        }
      },
    }),
    [idx],
  );

  const edit = () => {
    const onEdit = () => {
      if (id) {
        openEditEventStatusModal(refresh, state, []);
      } else {
        openCreateEventStatusModal(refresh, [], state);
      }
    };
    executeWithLicenceCheck(LicenceS.Restrictions.CUSTOM_EVENT_STATE, onEdit);
  };

  return (
    <tr ref={drop} style={{ opacity: isDragging ? 0.5 : 1 }}>
      <td>
        {idx !== null && lg && (
          <ActionIcon ref={drag}>
            {canDrop && <IconDragDrop color="#1D5191" size={isOver ? 28 : 20} />}
            {!canDrop && <IconDragDrop2 size={20} style={{ cursor: 'grab' }} />}
          </ActionIcon>
        )}
      </td>
      <td>
        <div>{color && <Dot withoutShadow colour={color} />}</div>
      </td>
      <td>
        <Text>{`${emoji ? `${emoji} ` : ''}${label}`}</Text>
      </td>
      {!xs && (
        <td>
          <Text>{i18n._(`event.state.${originState.toLocaleLowerCase()}`)}</Text>
        </td>
      )}
      <td>
        <Group spacing="xs" position="right">
          <IconBallpen size={20} color="gray" style={{ cursor: 'pointer' }} onClick={edit} />
          {id && !primary && (
            <IconTrashX
              size={20}
              color="gray"
              style={{ cursor: 'pointer' }}
              onClick={() => openDeleteEventStateModal(state, refresh, states)}
            />
          )}
        </Group>
      </td>
    </tr>
  );
};

export default EventStateItem;
